import { useState, useEffect, useRef, useCallback, useDeferredValue } from 'react';
import { Search, FileText, Package, Loader2 } from 'lucide-react';
import { useContentSuggestions } from '@/lib/hooks/public/use-content-search';
import { useProductSuggestions } from '@/lib/hooks/public/use-content-search';
import { cn } from '@/lib/utils';

/**
 * Strip every HTML tag except <b> and <strong> so Solr's highlight markup
 * renders visually but no other HTML can be injected via the API response.
 */
function sanitizeHighlightHtml(html: string): string {
  return html.replace(/<(?!\/?(b|strong)>)[^>]*>/gi, '');
}

/**
 * Strip ALL HTML tags to recover the plain text from a Solr highlight string.
 * Used when a suggestion is selected and we need the raw search term.
 */
function stripAllHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '');
}

interface SearchAutocompleteProps {
  /** Current search input value */
  value: string;
  /** Callback when the input value changes */
  onChange: (value: string) => void;
  /** Callback when the user submits the search (Enter or form submit) */
  onSubmit: () => void;
  /** Callback when a suggestion is selected from the dropdown */
  onSelectSuggestion: (text: string, type: 'product' | 'content') => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether to show content suggestions alongside product suggestions */
  showContentSuggestions?: boolean;
}

/**
 * Search input with autocomplete dropdown showing product names and content titles.
 *
 * This component wraps a search input and displays a dropdown with suggestions
 * from both the product search and content search APIs. Product suggestions show
 * a package icon, content suggestions show a file icon.
 *
 * Clicking a suggestion navigates to the search page with the keyword pre-filled.
 */
export function SearchAutocomplete({
  value,
  onChange,
  onSubmit,
  onSelectSuggestion,
  placeholder = 'Search...',
  className,
  showContentSuggestions = true,
}: SearchAutocompleteProps) {
  const [manuallyClosed, setManuallyClosed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const highlightedIndexRef = useRef(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce the query to avoid firing a request on every keystroke.
  // useDeferredValue lets React wait until the browser is idle before
  // updating the deferred copy, so rapid typing only triggers one request.
  const deferredQuery = useDeferredValue(value);

  // Fetch product suggestions using the debounced query
  const { data: productSuggestionsData = [], isLoading: productSuggestionsLoading } =
    useProductSuggestions(deferredQuery, 5, {
      enabled: deferredQuery.trim().length >= 2,
    });

  // Extract just the text strings from product suggestions
  const productSuggestions = productSuggestionsData.map((s) => s.text);

  // Fetch content suggestions using the debounced query
  const { data: contentSuggestions = [], isLoading: contentSuggestionsLoading } =
    useContentSuggestions(deferredQuery, 5, {
      enabled: showContentSuggestions && deferredQuery.trim().length >= 2,
    });

  const hasSuggestions =
    productSuggestions.length > 0 || contentSuggestions.length > 0;
  const isLoading = productSuggestionsLoading || contentSuggestionsLoading;

  // All suggestions flattened for keyboard navigation
  const allSuggestions = [
    ...productSuggestions.map((s) => ({ type: 'product' as const, text: s })),
    ...contentSuggestions.map((s) => ({ type: 'content' as const, text: s })),
  ];

  // Keep a ref in sync so handleKeyDown always reads the latest value
  // (avoids stale closure when rapid key events fire before re-render)
  const allSuggestionsRef = useRef(allSuggestions);
  allSuggestionsRef.current = allSuggestions;

  // Derive whether the dropdown should be open.
  // Open when: query is 2+ chars AND (has suggestions or loading) AND user hasn't manually closed it.
  // Reset manual close when the query changes.
  const shouldShowDropdown = value.trim().length >= 2 && (hasSuggestions || isLoading) && !manuallyClosed;

  // Reset highlighted index and manual-close flag when the query changes.
  // This ensures the dropdown reopens and highlight clears when the user types.
  useEffect(() => {
    setManuallyClosed(false);
    setHighlightedIndex(-1);
    highlightedIndexRef.current = -1;
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setManuallyClosed(true);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = useCallback(
    (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      inputRef.current?.blur();
      onSubmit();
    },
    [onSubmit],
  );

  const handleSelect = useCallback(
    (text: string, type: 'product' | 'content') => {
      // Solr suggestions include <b>...</b> highlight markup. Strip the HTML
      // before passing the value to the input/navigation so the search page
      // receives a clean keyword (e.g. "Craft" instead of "<b>Craft</b>").
      const plainText = stripAllHtml(text);
      onChange(plainText);
      setManuallyClosed(true);
      inputRef.current?.blur();
      onSelectSuggestion(plainText, type);
    },
    [onChange, onSelectSuggestion],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const currentSuggestions = allSuggestionsRef.current;

    if (!shouldShowDropdown || currentSuggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < currentSuggestions.length - 1 ? prev + 1 : 0;
          highlightedIndexRef.current = next;
          return next;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : currentSuggestions.length - 1;
          highlightedIndexRef.current = next;
          return next;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndexRef.current >= 0 && highlightedIndexRef.current < currentSuggestions.length) {
          const selected = currentSuggestions[highlightedIndexRef.current];
          handleSelect(selected.text, selected.type);
        } else {
          onSubmit();
        }
        break;
      case 'Escape':
        setManuallyClosed(true);
        break;
    }
  };

  // Unique id for the listbox, stable across renders
  const listboxId = useRef(`search-listbox-${Math.random().toString(36).slice(2, 9)}`).current;

  // Derive the active descendant id from the highlighted index
  const activeDescendant = highlightedIndex >= 0
    ? `${listboxId}-option-${highlightedIndex}`
    : undefined;

  return (
    <div className={cn('relative', className)}>
      <form onSubmit={handleSubmit} className="flex-1">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded={shouldShowDropdown}
            aria-controls={listboxId}
            aria-activedescendant={activeDescendant}
            aria-autocomplete="list"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              // Reopen dropdown if it was closed by Escape/click-outside
              // and there are suggestions to show
              if (value.trim().length >= 2 && (hasSuggestions || isLoading)) {
                setManuallyClosed(false);
              }
            }}
            autoComplete="off"
            className="w-full pr-10 h-12 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </div>
      </form>

      {shouldShowDropdown && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label="Search suggestions"
          className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-surface border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          {isLoading && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}

          {!isLoading && !hasSuggestions && value.trim().length >= 2 && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              No suggestions found
            </div>
          )}

          {/* Product suggestions */}
          {productSuggestions.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800">
                Products
              </div>
              {productSuggestions.map((suggestion, index) => (
                <button
                  key={`product-${suggestion}`}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  className={cn(
                    'w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
                    highlightedIndex === index &&
                      'bg-gray-100 dark:bg-gray-800',
                  )}
                  onClick={() => handleSelect(suggestion, 'product')}
                  onMouseEnter={() => {
                    setHighlightedIndex(index);
                    highlightedIndexRef.current = index;
                  }}
                >
                  <Package className="h-4 w-4 text-blue-500 shrink-0" />
                  <span
                    className="truncate"
                    // Product suggestions contain HTML <b> tags from Solr
                    // highlighting. Sanitize to only allow <b> / <strong>
                    // before injecting so user input can't inject arbitrary HTML.
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHighlightHtml(suggestion),
                    }}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Content suggestions */}
          {contentSuggestions.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800">
                Content
              </div>
              {contentSuggestions.map((suggestion, index) => {
                const globalIndex = productSuggestions.length + index;
                return (
                  <button
                    key={`content-${suggestion}`}
                    id={`${listboxId}-option-${globalIndex}`}
                    role="option"
                    aria-selected={highlightedIndex === globalIndex}
                    className={cn(
                      'w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors',
                      highlightedIndex === globalIndex &&
                        'bg-gray-100 dark:bg-gray-800',
                    )}
                    onClick={() => handleSelect(suggestion, 'content')}
                    onMouseEnter={() => {
                      setHighlightedIndex(globalIndex);
                      highlightedIndexRef.current = globalIndex;
                    }}
                  >
                    <FileText className="h-4 w-4 text-green-600 shrink-0" />
                    <span
                      className="truncate"
                      // Content suggestions contain HTML <b> tags from Solr
                      // highlighting. Sanitize to only allow <b> / <strong>
                      // before injecting so user input can't inject arbitrary HTML.
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHighlightHtml(suggestion),
                      }}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}